import {
    type ISitemapContentCategory,
    Routes,
    SitemapContent,
} from "@buildwithflux/core";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";
import type { MetaDescriptor } from "react-router";

import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { APP_DESCRIPTION, siteMetaDescriptors, toCanonicalUrl } from "../seo";
import { getProcessedImageUrl } from "../utils/imageUrl";

import DocumentThumb from "./common/DocumentThumb";
import MarkdownDescription from "./common/MarkdownDescription";

const absoluteUrl = toCanonicalUrl("/sitemap");

const PAGE_TITLE = "Flux Sitemap";

export function meta(): MetaDescriptor[] {
    return [
        { title: PAGE_TITLE },
        { name: "description", content: APP_DESCRIPTION },
        { tagName: "link", rel: "canonical", href: absoluteUrl },
        { tagName: "link", rel: "alternate", type: "text/markdown", href: `${absoluteUrl}.md` },
        { property: "og:title", content: PAGE_TITLE },
        { property: "og:description", content: APP_DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:url", content: absoluteUrl },
        { name: "twitter:title", content: "Flux - Sitemap" },
        { name: "twitter:description", content: APP_DESCRIPTION },
        ...siteMetaDescriptors(),
        {
            "script:ld+json": {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Home", item: "https://flux.ai" },
                    { "@type": "ListItem", position: 2, name: "Sitemap", item: absoluteUrl },
                ],
            },
        },
    ];
}

function CategoryUnit({ category }: { category: ISitemapContentCategory }) {
    const categoryUrl = `${Routes.SITEMAP}/${category.key}`;
    const thumbUrl = useMemo(
        () => getProcessedImageUrl(category.thumb, 124, "contain"),
        [category.thumb],
    );

    return (
        <Paper
            key={category.key}
            square
            component="li"
            sx={{
                mt: 1,
                p: 2,
                minHeight: "113px",
            }}
        >
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-start" }}>
                <Box sx={{ marginRight: 1, flexGrow: 1 }}>
                    <Link href={categoryUrl}>
                        <DocumentThumb thumbUrl={thumbUrl} documentName={category.name} size={124} />
                    </Link>
                </Box>

                <Box sx={{ maxWidth: "699px", flexGrow: 11 }}>
                    <Link href={categoryUrl} color="textPrimary">
                        <Typography color="textPrimary" variant="h6" component="h2">
                            {category.name}
                        </Typography>
                    </Link>

                    <Box>
                        <MarkdownDescription markdownText={category.description || ""} />
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
}

export default function SitemapPage() {
    useDocumentTitle(PAGE_TITLE);

    return (
        <>
            <Container maxWidth="md">
                <Grid container>
                    <Grid size={12}>
                        <Typography
                            color="text.primary"
                            variant="h2"
                            component="h1"
                            sx={{ wordBreak: "break-word", mb: 2 }}
                        >
                            Sitemap
                        </Typography>

                        <List aria-label="Categories">
                            {Object.values(SitemapContent).map((category) => (
                                <CategoryUnit key={category.key} category={category} />
                            ))}
                        </List>
                    </Grid>
                </Grid>
            </Container>
        </>
    );
}
